import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Avatar, styled, CircularProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createInvitedIssuer, validateJwtToken } from '../services/apiservice';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';
import Loader from '../components/ProgressBars/BladeLoader';
import InvalidTokenScreen from './InvalidTokenScreen';

const RedAsterisk = styled('span')({
  color: 'red',
});

const styles = {
  textCenter: {
    textAlign: 'center',
    color: '#666',
  },
  smallText: {
    fontSize: '0.8rem',
  },
};

const validationSchema = Yup.object({
  IssuerRepEmailID: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{8,}$/,
      'Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.'
    )
    .required('Password is required'),
  IssuerRepFirstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters long')
    .max(50, 'First name cannot exceed 50 characters'),
  IssuerRepLastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters long')
    .max(50, 'Last name cannot exceed 50 characters'),
});

const IssuerSignup = () => {
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const navigate = useNavigate();
  const [issuerAdminUserID, setIssuerAdminUserID] = useState(null);
  const [issuerCompanyID, setIssuerCompanyID] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await validateJwtToken(token);
        console.log(response)
        if (response.success) {
          setIsValidToken(true);
          setIssuerAdminUserID(response.IssuerAdminUserID);
          setIssuerCompanyID(response.IssuerCompanyID);
        } else if (response.message === 'Issuer account already created.') {
          setErrorMessage('Link has expired. Account has already been created.');
          setIsValidToken(false);
        } else {
          setIsValidToken(false);
          setErrorMessage('Invalid or expired token');
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setIsValidToken(false);
        setErrorMessage('Failed to validate token');
      } finally {
        setTokenLoading(false);
      }
    };
    if (token) {
      validateToken();
    } else {
      setIsValidToken(false);
      setErrorMessage('No token provided');
      setTokenLoading(false);
    }
  }, [token]);

  const formik = useFormik({
    initialValues: {
      IssuerRepFirstName: '',
      IssuerRepLastName: '',
      IssuerRepEmailID: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMessage('');
      setSuccessMessage('');
      setLoading(true);

      try {
        const response = await createInvitedIssuer(values.IssuerRepFirstName, values.IssuerRepLastName, values.IssuerRepEmailID, values.password, issuerAdminUserID, issuerCompanyID);
        if (response.success && response.message === 'Issuer created successfully') {
          setSuccessMessage('Issuer Created Successfully');
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setErrorMessage('Issuer already exists');
        }
      } catch (error) {
        console.error('Error creating Issuer:', error);
        if (error.response && error.response.data && error.response.data.message === 'Issuer already exists') {
          setErrorMessage('An Issuer account with this email already exists, please try a different one.');
        } else {
          setErrorMessage('Error creating Issuer');
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  if (tokenLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!isValidToken) {
    const errorHeader = errorMessage === 'Link has expired. Account has already been created.'
      ? 'Link has expired. Account has already been created.'
      : 'Invalid Token';

    return <InvalidTokenScreen errorMessage={errorHeader} />;
  }

  return (
    <Box
      style={{
        height: '100vh',
        background: 'linear-gradient(-20deg, #616161 0%, #9bc5c3 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Issuer SignUp</title>
      </Helmet>
      <Container component={Paper} elevation={6} maxWidth="xs" style={{
        padding: 30,
        borderRadius: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        zIndex: 1,

      }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <Avatar
                src='/cirapng.png'
                alt="Logo"
                style={{
                  width: 80,
                  height: 100,
                  marginBottom: 15,
                  overflow: 'visible',
                }}
              />
            </Grid>
          </Grid>
          <Typography variant="h5" component="h2" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold', color: '#333', marginBottom: 5 }}>
            Issuer Signup
          </Typography>
          <Typography variant="subtitle1" component="h2" gutterBottom style={{ ...styles.textCenter, ...styles.smallText, marginBottom: 15 }}>
            Join Credential Intelligence Recognition
          </Typography>
          {errorMessage && (
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              style={{
                ...styles.textCenter,
                marginBottom: 10,
                color: 'red',
                animation: 'blink 2s infinite',
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              style={{
                ...styles.textCenter,
                marginBottom: 10,
                color: 'green',
                animation: 'blink 2s infinite',
              }}
            >
              {successMessage}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Representative First Name'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="IssuerRepFirstName"
                variant="outlined"
                value={formik.values.IssuerRepFirstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.IssuerRepFirstName && Boolean(formik.errors.IssuerRepFirstName)}
                helperText={formik.touched.IssuerRepFirstName && formik.errors.IssuerRepFirstName}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Representative Last Name'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="IssuerRepLastName"
                variant="outlined"
                value={formik.values.IssuerRepLastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.IssuerRepLastName && Boolean(formik.errors.IssuerRepLastName)}
                helperText={formik.touched.IssuerRepLastName && formik.errors.IssuerRepLastName}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Representative Email'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="IssuerRepEmailID"
                type="email"
                variant="outlined"
                value={formik.values.IssuerRepEmailID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.IssuerRepEmailID && Boolean(formik.errors.IssuerRepEmailID)}
                helperText={formik.touched.IssuerRepEmailID && formik.errors.IssuerRepEmailID}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Password'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="password"
                type="password"
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                style={{
                  padding: 10,
                  position: 'relative',
                  height: '45px',
                }}
                disabled={formik.isSubmitting || loading}
              >
                <Box style={{ visibility: formik.isSubmitting || loading ? 'hidden' : 'visible' }}>
                  Sign Up
                </Box>
                {(formik.isSubmitting || loading) && (
                  <CircularProgress
                    color="primary"
                    size={28}
                    style={{
                      position: 'absolute',
                      top: '20%',
                      left: '48%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </Button>
            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: 5 }}>
              <Typography
                variant="subtitle1"
                component="h2"
                gutterBottom
                style={{ ...styles.textCenter }}
              >
                Already have an account?{' '}
                <Button color="primary" onClick={() => navigate('/login')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  Sign In
                </Button>
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Box>
  );
};

export default IssuerSignup;